import React, { useEffect, useMemo, useRef } from 'react'
import NumericKeypad from '../../../components/ui-components/NumericKeypad'
import CustomAmountInput from '../../../components/ui-components/CustomAmountInput'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { paymentPageConfig } from '../../../utils/mf-execution/paymentPageConfig'
import { PaymentPageType, InvestPaymentMethodTypes } from '../../../types/mf-execution.types'
import { useMFExecutionContext } from '../../../state-management/contexts/MFExecutionContext'
import { useSIPContext } from '../../../state-management/contexts/SIPContext'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { AllocationAmountAdjustmentToast } from '../../../components/execution/AllocationAmountAdjustmentToast'
import { useToast } from '../../../state-management/contexts/ToastContext'
import { PaymentPageHeader } from '../../../components/execution/PaymentPageHeader'
import { useInputKeypad } from '../../../hooks/mf-execution/useInputkeypad'
import { useProcessingPartnerMaintenanceBottomSheet } from '../../../hooks/mf-execution/useProcessingPartnerMaintenanceBottomSheet'
import { BuyPaymentMethodsButton } from '../../../components/execution/buy/BuyPaymentMethodsButton'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { CalendarBottomSheet } from '../../../components/BottomSheetComponents/mf-execution/calendar/CalendarBottomSheet'
import { ToggleChip } from '../../../components/execution/ToggleChip'
import { CalendarButton } from '../../../components/execution/CalendarButton'
import { SIPConfirmationBottomSheet } from '../../../components/BottomSheetComponents/mf-execution/sip/SIPConfirmationBottomSheet'
import { getFrequencyText, getNextSIPDate, getWhyNotSIPDate } from '../../../utils/mf-execution/calendar'
import { FrequencyType } from '../../../types/calendar.types'
import { WhyNotSIPBottomSheet } from '../../../components/BottomSheetComponents/mf-execution/calendar/WhyNotSIPBottomSheet'
import { LoadingSpinnerBase } from '../../../components/LoadingSpinnerBase'
import {
  getSIPMinAmount,
  getSIPMaxAmount,
  getSIPStepper,
  getAvailableMonthlyDays,
  getAvailableWeeklyDays,
  getAvailableDailyDays
} from '../../../utils/mf-execution/sipDataUtils'
import { getBottomPadding, getTopPadding } from '../../../utils/sessionStorage'
import { convertNumberToCurrency } from '../../../utils/utils'
import { getEffectiveMaxAmount, getEffectiveStepper } from '../../../utils/mf-execution/paymentAmount'
import { EventNames } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'
import {
  buildOrderOtpPaymentFields,
  getUpiIntentDisplayForPaymentMethod,
} from '../../../utils/mf-execution/paymentMethodSelection'
import { isUpiIntentsFlowEnabled } from '../../../utils/mf-execution/upiIntentsFeature'

enum ErrorType {
  MIN = 'min',
  MAX = 'max',
  STEPPER = 'stepper'
}

const SIPPaymentPage: React.FC = () => {
  const { mfExecutionData, isMFExecutionDataLoading } = useMFExecutionContext()
  const [searchParams] = useSearchParams()
  const isConfirm = searchParams.get('isConfirm')
  const {
    paymentMethods,
    bankAccountDetails,
    defaultPaymentMethodDetails,
    selectedPaymentMethod,
    handlePaymentMethodChange,
    amount,
    initialShownAmount,
    handleAmountChange,
    fromAdvisory,
    fromApp,
    amountAdjustmentReason,
    clearAmountAdjustmentReason,
    handleOTPTrigger,
    calendarSelection,
    handleCalendarSelection,
    installedUpiApps,
    requestUpiIntents,
    upiIntentMaximumAllowedAmount,
  } = useSIPContext()
  const { openBottomSheet, closeBottomSheet, destroyBottomSheet, openNestedBottomSheet, closeNestedBottomSheet } =
    useBottomSheet()
  const navigate = useNavigate()
  const { isin, data: metaData } = useMetaDataContext()
  const fundName = metaData?.fundDetails?.fundName ?? ''
  const trackingFund = isin && fundName ? { fundIsin: isin, fundName } : undefined
  const paymentType = PaymentPageType.SIP
  const { placeholder, errorHelpText } = paymentPageConfig[paymentType as PaymentPageType]

  useProcessingPartnerMaintenanceBottomSheet('SIP payment')

  const inputRef = useRef<HTMLDivElement>(null)
  const { showToast } = useToast()

  const minAmount = useMemo(() => {
    return getSIPMinAmount(mfExecutionData?.sip, calendarSelection?.frequencyType ?? FrequencyType.MONTHLY) ?? 0
  }, [calendarSelection?.frequencyType, mfExecutionData?.sip])

  const maximumAllowedAmount = useMemo(() => {
    return getEffectiveMaxAmount(
      getSIPMaxAmount(mfExecutionData?.sip, calendarSelection?.frequencyType ?? FrequencyType.MONTHLY) ?? 0,
      paymentMethods
    )
  }, [calendarSelection?.frequencyType, mfExecutionData?.sip, paymentMethods])

  const stepper = useMemo(() => {
    return getEffectiveStepper(
      getSIPStepper(mfExecutionData?.sip, calendarSelection?.frequencyType ?? FrequencyType.MONTHLY)
    )
  }, [calendarSelection?.frequencyType, mfExecutionData?.sip])

  const {
    handleBackspaceClick,
    handleCursorChange,
    handleNumberClick,
    errorType,
    cursorPos,
    enteredAmount,
    triggerValidation
  } = useInputKeypad({
    minAmount: minAmount ?? 0,
    maxAmount: maximumAllowedAmount ?? 0,
    stepper,
    defaultAmount: amount,
    validationTrigger: calendarSelection.frequencyType
  })

  useEffect(() => {
    if (enteredAmount) {
      handleAmountChange(parseFloat(enteredAmount))
    } else {
      handleAmountChange(0)
    }
  }, [enteredAmount, calendarSelection.frequencyType])

  const navigateToOtp = () => {
    navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.SIP}/otp`)
  }

  const handleFinalAction = () => {
    if (!selectedPaymentMethod?.paymentMethodId) return

    const otpPaymentFields = buildOrderOtpPaymentFields(selectedPaymentMethod, isUpiIntentsFlowEnabled())

    handleOTPTrigger({
      paymentMethodId: selectedPaymentMethod?.paymentMethodId ?? '',
      paymentMethodType: otpPaymentFields.paymentMethodType,
      transactionType: 'SIP',
      isin: isin ?? '',
      amount,
      frequencyValue: calendarSelection?.value ?? 1,
      frequencyType: calendarSelection?.frequencyType,
      ...(otpPaymentFields.upiPackageName ? { upiPackageName: otpPaymentFields.upiPackageName } : {}),
    })
  }

  const paymentMethodDisplay = getUpiIntentDisplayForPaymentMethod(
    selectedPaymentMethod,
    installedUpiApps
  )

  const getErrorMessage = () => {
    if (!errorType) return null

    switch (errorType) {
      case ErrorType.MIN:
        return `Minimum ${errorHelpText} is ${convertNumberToCurrency({ amount: minAmount ?? 0, casing: 'LOWER' })}`
      case ErrorType.MAX:
        return `Maximum ${errorHelpText} is ${convertNumberToCurrency({ amount: maximumAllowedAmount ?? 0, casing: 'LOWER' })}`
      case ErrorType.STEPPER:
        return stepper
          ? `Enter amount in multiples of ${convertNumberToCurrency({ amount: stepper, casing: 'LOWER' })}`
          : 'Invalid amount'
      default:
        return null
    }
  }

  const showConfirmationBottomSheet = () => {
    const confirmationPaymentMethodType = buildOrderOtpPaymentFields(selectedPaymentMethod, isUpiIntentsFlowEnabled())
      .paymentMethodType as InvestPaymentMethodTypes
    openBottomSheet(
      <SIPConfirmationBottomSheet
        onDismiss={closeBottomSheet}
        ScreenName='SIPPaymentPage'
        amount={amount}
        displayAccNumber={selectedPaymentMethod?.displayAccNumber ?? ''}
        trackingFund={trackingFund}
        onPlaceOrder={() => {
          trackEvent({
            eventName: EventNames.ButtonTap,
            payload: {
              ScreenName: 'SIP payment',
              buttonLabel:
                confirmationPaymentMethodType === InvestPaymentMethodTypes.MANDATE ? 'Place order' : 'Continue to pay',
              eventId: 'MFE_BS_REVIEW_DETAILS_CTA_PLACE_ORDER',
              ...(trackingFund ?? {})
            }
          })
          handleFinalAction()
          destroyBottomSheet()
          navigateToOtp()
        }}
        sipDate={getFrequencyText(calendarSelection)}
        firstSIPDate={'Today'}
        nextSIPDate={
          getNextSIPDate(
            calendarSelection,
            getAvailableMonthlyDays(mfExecutionData?.sip),
            getAvailableWeeklyDays(mfExecutionData?.sip),
            getAvailableDailyDays(mfExecutionData?.sip)
          ) ?? new Date().getTime()
        }
        whyNotSIPDate={
          getWhyNotSIPDate(
            calendarSelection,
            getAvailableMonthlyDays(mfExecutionData?.sip),
            getAvailableWeeklyDays(mfExecutionData?.sip),
            getAvailableDailyDays(mfExecutionData?.sip)
          ) ?? null
        }
        onWhyNotSIPClick={(nextSIPDate, whyNotSIPDate) => {
          openNestedBottomSheet(
            <WhyNotSIPBottomSheet
              nextSIPDate={nextSIPDate}
              whyNotSIPDate={whyNotSIPDate}
              onClose={closeNestedBottomSheet}
            />
          )
        }}
        upiId={paymentMethodDisplay.appName || selectedPaymentMethod?.upiId || ''}
        paymentMethodIconUrl={
          isUpiIntentsFlowEnabled() ? paymentMethodDisplay.appLogoUrl : undefined
        }
        paymentMethodType={confirmationPaymentMethodType}
        frequencyType={calendarSelection?.frequencyType || FrequencyType.MONTHLY}
      />
    )
  }

  const handlePayNowClick = (method: 'ADD_UPI' | 'PAY_NOW') => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'SIP payment',
        buttonLabel: method === 'ADD_UPI' ? 'Add UPI' : 'Continue',
        eventId: method === 'ADD_UPI' ? 'MFE_SELECT_AMOUNT_CTA_ADD_UPI' : 'MFE_SELECT_AMOUNT_CTA_PAY_NOW',
        ...(trackingFund ?? {})
      }
    })
    // Trigger validation before proceeding and check result
    const validationError = triggerValidation()

    // Check if there are validation errors
    if (validationError) {
      return
    }

    if (method === 'ADD_UPI') {
      navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.SIP}/add-upi`)
      return
    }

    // Shown amount vs continued amount, only after validation passes
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'SIP payment',
        buttonLabel: 'Continue',
        eventId: 'MFE_SELECT_AMOUNT_CONTINUED',
        amount,
        responseName: initialShownAmount != null ? String(initialShownAmount) : '',
        ...(trackingFund ?? {})
      }
    })
    showConfirmationBottomSheet()
  }

  const handleAddUPIClick = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'SIP payment',
        buttonLabel: 'Proceed',
        eventId: 'MFE_SELECT_AMOUNT_CTA_ADD_UPI',
        ...(trackingFund ?? {})
      }
    })
    const validationError = triggerValidation()
    if (validationError) {
      return
    }
    navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.SIP}/add-upi`)
  }

  useEffect(() => {
    if (isConfirm && selectedPaymentMethod) {
      handlePayNowClick('PAY_NOW')
      searchParams.delete('isConfirm')
      const fromParam = fromApp ? 'from=APP' : ''
      const fromAdvisoryParam = fromAdvisory ? 'fromAdvisory=true' : ''
      const query = [fromParam, fromAdvisoryParam].filter(Boolean).join('&')
      navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.SIP}${query ? `?${query}` : ''}`, { replace: true })
    }
  }, [isConfirm])

  const handleCalendarClick = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: 'SIP payment',
        eventId: 'MFE_SELECT_AMOUNT_CTA_SELECT_FREQ',
        buttonLabel: 'Select frequency',
        ...(trackingFund ?? {})
      }
    })
    openBottomSheet(
      <CalendarBottomSheet
        onDismiss={closeBottomSheet}
        ScreenName='SIPPaymentPage'
        trackingFund={trackingFund}
        selectedFrequency={calendarSelection.frequencyType}
        availableMonthlyDays={getAvailableMonthlyDays(mfExecutionData?.sip)}
        availableWeeklyDays={getAvailableWeeklyDays(mfExecutionData?.sip)}
        availableDailyDays={getAvailableDailyDays(mfExecutionData?.sip)}
        selectedValue={calendarSelection?.value}
        onSelectionChange={(selection) => {
          handleCalendarSelection(selection)
        }}
        onWhyNotSIPClick={(nextSIPDate, whyNotSIPDate) => {
          openNestedBottomSheet(
            <WhyNotSIPBottomSheet
              nextSIPDate={nextSIPDate}
              whyNotSIPDate={whyNotSIPDate}
              onClose={closeNestedBottomSheet}
            />
          )
        }}
      />
    )
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'SIP payment',
        ...(trackingFund ?? {})
      }
    })
  }, [])

  if (isMFExecutionDataLoading) {
    return (
      <div
        style={{
          paddingTop: `calc(${getTopPadding()}px + 16px)`,
          paddingBottom: `calc(${getBottomPadding()}px + 16px)`
        }}
        className='w-full h-screen flex flex-col justify-between bg-white px-5'
      >
        <div className='w-full flex-shrink-0'>
          <PaymentPageHeader />
        </div>
        <div className='w-full h-full flex flex-col flex-1 justify-center items-center'>
          <LoadingSpinnerBase />
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        paddingTop: `calc(${getTopPadding()}px + 16px)`,
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`
      }}
      className='w-full h-screen flex flex-col justify-between bg-white px-5'
    >
      <div className='w-full'>
        <PaymentPageHeader />
      </div>

      <div className='w-full flex flex-col justify-center flex-1'>
        <div className='flex flex-col gap-2 w-full'>
          <div className='h-8'>
            <div className='flex gap-2'>
              <ToggleChip
                testLocator='MFE_SELECT_AMOUNT_CTA_SIP'
                isLocked={fromAdvisory}
                options={[
                  {
                    id: PaymentPageType.SIP,
                    label: 'SIP'
                  },
                  {
                    id: PaymentPageType.BUY,
                    label: 'One-time'
                  }
                ]}
                defaultSelectedId={PaymentPageType.SIP}
                onSelectionChange={(selectedId) => {
                  trackEvent({
                    eventName: EventNames.UserClicked,
                    payload: {
                      ScreenName: 'SIP payment',
                      eventId: 'MFE_SELECT_AMOUNT_CTA_SIP',
                      buttonLabel: selectedId === PaymentPageType.SIP ? 'SIP' : 'One-time',
                      ...(trackingFund ?? {})
                    }
                  })
                  if (mfExecutionData?.buttonDisabledStates?.buy?.isDisabled) {
                    showToast({
                      message: 'New one-time investments paused by fund house',
                      toastToBeShownById: 'SIPPaymentPage'
                    })
                    return
                  }
                  const amountParam = amount > 0 ? `amount=${amount}` : ''
                  const fromParam = fromApp ? 'from=APP' : ''
                  const fromAdvisoryParam = fromAdvisory ? 'fromAdvisory=true' : ''
                  const query = [amountParam, fromParam, fromAdvisoryParam].filter(Boolean).join('&')
                  navigate(`/mutual-fund/${isin}/payment/${selectedId}${query ? `?${query}` : ''}`, { replace: true })
                }}
              />
            </div>
          </div>
          <div className='h-[74px] flex items-center w-full animate-fade-150'>
            <CustomAmountInput
              value={amount ? `${amount}` : ''}
              cursorPos={cursorPos}
              onCursorChange={handleCursorChange}
              inputRef={inputRef}
              placeholder={placeholder ?? ''}
            />
          </div>
          <div className='h-10 w-full'>
            {errorType ? (
              <div className='text-[#E77464] text-[14px] font-[500] animate-fade-150'>{getErrorMessage()}</div>
            ) : (
              <div className='pt-2'>
                <div className='flex gap-2'>
                  <div test-locator='MFE_SELECT_AMOUNT_CTA_SELECT_FREQ' onClick={handleCalendarClick}>
                    <CalendarButton selection={calendarSelection} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='w-full h-max pt-2 pb-5'>
        <AllocationAmountAdjustmentToast
          amountAdjustmentReason={amountAdjustmentReason}
          amount={amount}
          toastToBeShownById='SIPPaymentPage'
          clearAmountAdjustmentReason={clearAmountAdjustmentReason}
        />
        <div className='w-full h-[70px]'>
          {paymentMethods && (
            <BuyPaymentMethodsButton
              amount={amount}
              isDisabled={!!errorType || !amount}
              handlePayNowClick={handlePayNowClick}
              selectedPaymentMethod={selectedPaymentMethod}
              handlePaymentMethodChange={handlePaymentMethodChange}
              paymentMethods={paymentMethods}
              paymentType={paymentType}
              trackingFund={trackingFund}
              handleAddUPIClick={handleAddUPIClick}
              installedUpiApps={installedUpiApps}
              bankAccountDetails={bankAccountDetails}
              defaultPaymentMethodDetails={defaultPaymentMethodDetails}
              requestUpiIntents={requestUpiIntents}
              upiIntentMaximumAllowedAmount={upiIntentMaximumAllowedAmount}
            />
          )}
        </div>
      </div>
      <div className='w-full px-5'>
        <NumericKeypad onNumberClick={handleNumberClick} onBackspaceClick={handleBackspaceClick} />
      </div>
    </div>
  )
}

export default SIPPaymentPage
